import supabase from '../supabaseClient';
import { PREMIUM_FEATURES } from './subscriptionUtils';

export interface ClientAccessInfo {
  /** Set of telegram_user_ids the trainer can currently see/use. `null` means unrestricted (premium). */
  allowedTelegramIds: Set<string> | null;
  /** How many of the trainer's clients are currently locked out by the free tier cap. */
  lockedCount: number;
}

/** Apply free-tier cap to an in-memory roster (oldest clients stay active). */
export const computeClientAccessFromList = (
  clients: { telegram_user_id: string; created_at: string }[],
  isPremium: boolean
): ClientAccessInfo => {
  if (isPremium) {
    return { allowedTelegramIds: null, lockedCount: 0 };
  }

  const sorted = [...clients].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  const freeLimit = PREMIUM_FEATURES.CLIENT_LIMIT_FREE;
  const allowedTelegramIds = new Set(sorted.slice(0, freeLimit).map((c) => c.telegram_user_id));
  const lockedCount = Math.max(0, sorted.length - freeLimit);

  return { allowedTelegramIds, lockedCount };
};

/**
 * The free tier keeps only the oldest CLIENT_LIMIT_FREE clients (by signup order) active.
 * Everyone added after that stays in the database untouched but is hidden from the app
 * until the trainer resubscribes or their roster shrinks back under the cap.
 */
export const getClientAccessInfo = async (
  trainerId: string,
  isPremium: boolean
): Promise<ClientAccessInfo> => {
  if (isPremium) {
    return { allowedTelegramIds: null, lockedCount: 0 };
  }

  const { data, error } = await supabase
    .from('trainers_clients')
    .select('telegram_user_id, created_at')
    .eq('trainer_id', trainerId)
    .order('created_at', { ascending: true });

  if (error || !data) {
    return { allowedTelegramIds: null, lockedCount: 0 };
  }

  return computeClientAccessFromList(data, false);
};

export const filterToAllowedClients = <T extends { telegram_user_id: string }>(
  clients: T[],
  allowedTelegramIds: Set<string> | null
): T[] => {
  if (allowedTelegramIds === null) return clients;
  return clients.filter((client) => allowedTelegramIds.has(client.telegram_user_id));
};

export const filterToLockedClients = <T extends { telegram_user_id: string }>(
  clients: T[],
  allowedTelegramIds: Set<string> | null
): T[] => {
  if (allowedTelegramIds === null) return [];
  return clients.filter((client) => !allowedTelegramIds.has(client.telegram_user_id));
};

export const isClientLocked = (
  telegramUserId: string | null | undefined,
  allowedTelegramIds: Set<string> | null
): boolean => {
  if (allowedTelegramIds === null || !telegramUserId) return false;
  return !allowedTelegramIds.has(telegramUserId);
};
